import React, { useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  createSlug,
  parse,
  stringify,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { ensureCurrentUser } from '../../util/data';
import {
  getCurrentUserTypeRoles,
  initialValuesForUserFields,
  isUserAuthorized,
  pickUserFieldsData,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';

import {
  H3,
  Page,
  UserNav,
  NamedLink,
  LayoutSingleColumn,
  NamedRedirect,
  Modal,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import ProfileSettingsForm from './ProfileSettingsForm/ProfileSettingsForm';

import { updateProfile, uploadImage } from './ProfileSettingsPage.duck';
import css from './ProfileSettingsPage.module.css';

export const draftId = '00000000-0000-0000-0000-000000000000';
export const FARM_SITTER_WELCOME_PARAM = 'welcomeFarmSitter';

export const getProfileListingRedirectProps = publicData => {
  const { profileTitle, profileListingId, listingState } = publicData;
  const redirectProps = profileListingId
    ? {
        name: 'EditListingPage',
        params: {
          id: profileListingId,
          slug: createSlug(profileTitle),
          tab: 'details',
          type: listingState === 'draft' ? 'draft' : 'edit',
        },
      }
    : {
        name: 'EditListingPage',
        params: { slug: 'draft', id: draftId, type: 'new', tab: 'details' },
      };

  return redirectProps;
};

export const onImageUploadHandler = (values, fn) => {
  const { id, imageId, file } = values;
  if (file) {
    fn({ id, imageId, file });
  }
};

const ViewProfileLink = props => {
  const { userUUID, isUnauthorizedUser } = props;
  return userUUID && isUnauthorizedUser ? (
    <NamedLink
      className={css.profileLink}
      name="ProfilePageVariant"
      params={{ id: userUUID, variant: PROFILE_PAGE_PENDING_APPROVAL_VARIANT }}
    >
      <FormattedMessage id="ProfileSettingsPage.viewProfileLink" />
    </NamedLink>
  ) : userUUID ? (
    <NamedLink className={css.profileLink} name="ProfilePage" params={{ id: userUUID }}>
      <FormattedMessage id="ProfileSettingsPage.viewProfileLink" />
    </NamedLink>
  ) : null;
};

/**
 * ProfileSettingsPage
 *
 * @component
 * @param {Object} props
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {Object} props.image - The image
 * @param {string} props.image.id - The image id
 * @param {propTypes.uuid} props.image.imageId - The image id
 * @param {File} props.image.file - The image file
 * @param {propTypes.image} props.image.uploadedImage - The uploaded image
 * @param {Function} props.onImageUpload - The image upload function
 * @param {Function} props.onUpdateProfile - The update profile function
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {boolean} props.updateInProgress - Whether the update is in progress
 * @param {propTypes.error} props.updateProfileError - The update profile error
 * @param {propTypes.error} props.uploadImageError - The upload image error
 * @param {boolean} props.uploadInProgress - Whether the upload is in progress
 * @param {Function} props.onManageDisableScrolling - The manage disable scrolling function
 * @returns {JSX.Element}
 */
export const ProfileSettingsPageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const {
    currentUser,
    image,
    onImageUpload,
    onUpdateProfile,
    onManageDisableScrolling,
    scrollingDisabled,
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
  } = props;

  const searchParams = parse(location.search);
  const showFarmSitterWelcome = searchParams[FARM_SITTER_WELCOME_PARAM] === true;
  const [isFarmSitterModalOpen, setFarmSitterModalOpen] = useState(showFarmSitterWelcome);
  // Set synchronously (before onUpdateProfile is dispatched) so that the render triggered
  // by the currentUser update (which happens before the onUpdateProfile promise resolves)
  // doesn't redirect away via the provider check below, pre-empting the welcome reload.
  const pendingFarmSitterRedirectRef = useRef(false);

  const currentUserRole = getCurrentUserTypeRoles(config, currentUser);
  const { userFields, userTypes = [] } = config.user;
  const publicUserFields = userFields.filter(uf => uf.scope === 'public');

  const closeFarmSitterModal = () => {
    setFarmSitterModalOpen(false);
    const { [FARM_SITTER_WELCOME_PARAM]: _removed, ...restParams } = searchParams;
    history.replace({ search: stringify(restParams) });
  };

  const handleSubmit = (values, userType) => {
    const { firstName, lastName, displayName, bio: rawBio, becomeFarmSitter, ...rest } = values;
    const isBecomingFarmSitter = becomeFarmSitter === 'yes';

    const displayNameMaybe = displayName
      ? { displayName: displayName.trim() }
      : { displayName: null };

    // Ensure that the optional bio is a string
    const bio = rawBio || '';

    const profile = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      ...displayNameMaybe,
      bio,
      publicData: {
        ...pickUserFieldsData(rest, 'public', userType, userFields),
        ...(isBecomingFarmSitter ? { userType: 'farm-sitter' } : {}),
      },
    };
    const uploadedImage = props.image;

    // Update profileImage only if file system has been accessed
    const updatedValues =
      uploadedImage && uploadedImage.imageId && uploadedImage.file
        ? { ...profile, profileImageId: uploadedImage.imageId }
        : profile;

    if (isBecomingFarmSitter) {
      pendingFarmSitterRedirectRef.current = true;
    }

    onUpdateProfile(updatedValues).then(action => {
      const requestSucceeded = action?.meta?.requestStatus === 'fulfilled';
      if (requestSucceeded && isBecomingFarmSitter) {
        const nextSearch = stringify({ ...searchParams, [FARM_SITTER_WELCOME_PARAM]: true });
        window.location.href = `${window.location.pathname}?${nextSearch}`;
      } else {
        pendingFarmSitterRedirectRef.current = false;
      }
    });
  };

  const user = ensureCurrentUser(currentUser);
  const { firstName, lastName, displayName, bio, publicData } = user?.attributes.profile;
  // I.e. the status is active, not pending-approval or banned
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);

  const { userType } = publicData || {};
  const profileImageId = user.profileImage ? user.profileImage.id : null;
  const profileImage = image || { imageId: profileImageId };
  const userTypeConfig = userTypes.find(config => config.userType === userType);
  const isDisplayNameIncluded = userTypeConfig?.defaultUserFields?.displayName !== false;
  // ProfileSettingsForm decides if it's allowed to show the input field.
  const displayNameMaybe = isDisplayNameIncluded && displayName ? { displayName } : {};

  const profileSettingsForm = user.id ? (
    <ProfileSettingsForm
      className={css.form}
      currentUser={currentUser}
      initialValues={{
        firstName,
        lastName,
        ...displayNameMaybe,
        bio,
        profileImage: user.profileImage,
        ...initialValuesForUserFields(publicData, 'public', userType, userFields),
      }}
      profileImage={profileImage}
      onImageUpload={e => onImageUploadHandler(e, onImageUpload)}
      uploadInProgress={uploadInProgress}
      updateInProgress={updateInProgress}
      uploadImageError={uploadImageError}
      updateProfileError={updateProfileError}
      onSubmit={values => handleSubmit(values, userType)}
      marketplaceName={config.marketplaceName}
      userFields={publicUserFields}
      userTypeConfig={userTypeConfig}
      showBecomeFarmSitterField={userType === 'farmer'}
    />
  ) : null;

  const title = intl.formatMessage({ id: 'ProfileSettingsPage.title' });

  const showManageListingsLink = showCreateListingLinkForUser(config, currentUser);

  if (currentUserRole.provider && !isFarmSitterModalOpen && !pendingFarmSitterRedirectRef.current) {
    return <NamedRedirect {...getProfileListingRedirectProps(publicData)} />;
  }

  return (
    <Page className={css.root} title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={
          <>
            <TopbarContainer />
            <UserNav
              currentPage="ProfileSettingsPage"
              showManageListingsLink={showManageListingsLink}
            />
          </>
        }
        footer={<FooterContainer />}
      >
        <div className={css.content}>
          <div className={css.headingContainer}>
            <H3 as="h1" className={css.heading}>
              <FormattedMessage id="ProfileSettingsPage.heading" />
            </H3>

            <ViewProfileLink userUUID={user?.id?.uuid} isUnauthorizedUser={isUnauthorizedUser} />
          </div>
          {profileSettingsForm}
        </div>
      </LayoutSingleColumn>
      <Modal
        id="ProfileSettingsPage.farmSitterWelcomeModal"
        isOpen={isFarmSitterModalOpen}
        onClose={closeFarmSitterModal}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <H3 as="h2" className={css.farmSitterWelcomeModalTitle}>
          <FormattedMessage id="ProfileSettingsPage.farmSitterWelcomeModalTitle" />
        </H3>
        <p className={css.farmSitterWelcomeModalMessage}>
          <FormattedMessage id="ProfileSettingsPage.farmSitterWelcomeModalMessage" />
        </p>
        <NamedLink
          className={css.farmSitterWelcomeModalCta}
          {...getProfileListingRedirectProps(publicData)}
        >
          <FormattedMessage id="ProfileSettingsPage.farmSitterWelcomeModalCta" />
        </NamedLink>
      </Modal>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    image,
    uploadImageError,
    uploadInProgress,
    updateInProgress,
    updateProfileError,
  } = state.ProfileSettingsPage;
  return {
    currentUser,
    image,
    scrollingDisabled: isScrollingDisabled(state),
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onImageUpload: data => dispatch(uploadImage(data)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ProfileSettingsPage = compose(connect(mapStateToProps, mapDispatchToProps))(
  ProfileSettingsPageComponent
);

export default ProfileSettingsPage;
